<script lang="ts" setup>
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import type ResponseData from '@/types/ResponseData'
import type FakturacneUdaje from '@/types/FakturacneUdaje'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Headquarters from '@/types/Headquarters'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import CompanyMemberDataService from '@/services/CompanyMemberDataService'
import type CompanyMember from '@/types/CompanyMember'
import { onMounted, ref, computed } from 'vue';
import store from '@/store'
import OrderDataService from '@/services/OrderDataService'
import {
    MinusIcon,
    PlusIcon
} from '@heroicons/vue/outline'

const currentOrder = computed(() => store.state.currentOrder);

const currentOrderAddress = ref({} as Address);
const currentCompanyAddress = ref({} as Address);
const currentUserAddress = ref({} as Address);
const currentFakturacneUdaje = ref({} as FakturacneUdaje);
const currentUser = ref({} as User);
const currentCompany = ref({} as Company);
const currentHeadquarters = ref({} as Headquarters);
const currentCompanyMembers = ref([] as CompanyMember[]);
const items = ref([] as any[]);
const tab = ref(1);
const companySubStatuses = ref([] as any);
const currentCompanyUpdateOrder = ref({} as any);
const actual_company = ref({} as any);
const updated_company = ref({} as any);
const headquartersTypes = ref([] as any);

function currentTab(tabNumber: number) {
    tab.value = tabNumber
}

async function getHeadquartersTypes() {
    HeadquartersDataService.getHeadquartersTypes()
        .then((response: ResponseData) => {
            headquartersTypes.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUser(id: any) {
    await UserDataService.getUserById(id)
        .then(async (response: ResponseData) => {
            currentUser.value = response.data.data
            if (currentUser.value.address_id > 0)
                await getUserAddress(currentUser.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: any) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
            getCompanyHeadquarters(currentCompany.value.headquarters_id)
            getCompanyMembers(currentCompany.value.id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyMembers(id: any) {
    await CompanyMemberDataService.getAllCompanyMembersForCompany(id)
        .then((response: ResponseData) => {
            currentCompanyMembers.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getOrderAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentOrderAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUserAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentUserAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentCompanyAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyHeadquarters(id: any) {
    await HeadquartersDataService.getHeadquartersById(id)
        .then(async (response: ResponseData) => {
            currentHeadquarters.value = response.data.data
            if (currentHeadquarters.value.address_id > 0)
                await getCompanyAddress(currentHeadquarters.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getFakturacneUdaje(id: any) {
    await UserDataService.getFakturacneUdajeById(id)
        .then(async (response: ResponseData) => {
            currentFakturacneUdaje.value = response.data.data
            if (currentFakturacneUdaje.value.address_id > 0)
                await getOrderAddress(currentFakturacneUdaje.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanySubStatuses() {
    CompanyDataService.getCompanySubStatuses()
        .then((response: ResponseData) => {
            companySubStatuses.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function updateSubStatus() {
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .then(() => {
            toast.success('Stav žiadosti bol úspešne zmenený');
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });
}

async function getCompanyUpdateOrder() {
    OrderDataService.getCompanyUpdateOrder(currentOrder.value.id)
        .then((response: ResponseData) => {
            currentCompanyUpdateOrder.value = response.data.data
            actual_company.value = JSON.parse(currentCompanyUpdateOrder.value.actual_company)
            updated_company.value = JSON.parse(currentCompanyUpdateOrder.value.updated_company)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function refreshData() {
    items.value = JSON.parse(currentOrder.value.items)

    await getFakturacneUdaje(currentOrder.value.fakturacne_udaje_id)
    await getUser(currentOrder.value.user_id)
    if (currentOrder.value.company_id !== null) {
        await getCompany(currentOrder.value.company_id)
    }
    await getCompanySubStatuses();

    if (currentOrder.value.order_type == "company_edit") {
        await getCompanyUpdateOrder();
    }

    await getHeadquartersTypes();
}

onMounted(async () => {
    await refreshData();
})
</script>

<template>
    <div class="max-w-4xl mx-auto">
        <!-- Header Card -->
        <div class="bg-white rounded-xl shadow p-6 border border-gray-100 mb-6 flex flex-col gap-4">
            <div class="flex flex-row items-center gap-3">
                <svg class="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24" title="Zmena firmy (živnosť)">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <span class="text-2xl font-bold text-slate-900">Zmena firmy (živnosť)</span>
                <span v-if="items.length && items[0].price" class="ml-2 text-xl font-semibold text-teal-600"
                    title="Celková cena">- {{ items[0].price }}€</span>
            </div>
            <div class="flex items-center gap-2 text-lg font-semibold text-slate-700">
                <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24" title="Názov firmy">
                    <path d="M16 7a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                </svg>
                {{ currentCompany.name }}
            </div>
            <div class="text-xl text-slate-600">{{ currentOrder.description }}</div>
            <div class="flex flex-col gap-2 mt-2">
                <label for="sub_status" class="block text-sm font-medium text-gray-700">Stav žiadosti</label>
                <select v-model="currentCompany.sub_status" name="sub_status" id="sub_status"
                    @change="updateSubStatus()"
                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                    <option v-for="status in companySubStatuses" :value="status.id"
                        :selected="status.id == currentCompany.sub_status">{{ status.description }}</option>
                </select>
            </div>
        </div>
        <!-- Tabs -->
        <div class="flex flex-row justify-center gap-2 mb-6">
            <button @click="currentTab(1)" type="button"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition', tab === 1 ? 'bg-teal-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 border border-gray-200']"
                title="Faktúrované služby">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                        fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Faktúrované služby
            </button>
            <button v-if="currentOrder.order_type == 'company_edit'" @click="currentTab(2)" type="button"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition', tab === 2 ? 'bg-teal-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 border border-gray-200']"
                title="Zmeny v spoločnosti">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Zmeny v spoločnosti
            </button>
            <button @click="currentTab(3)" type="button"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition', tab === 3 ? 'bg-teal-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 border border-gray-200']"
                title="Podnikateľské údaje">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M16 17l-4 4m0 0l-4-4m4 4V3" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" />
                </svg>
                Podnikateľské údaje
            </button>
        </div>
        <div class="bg-white rounded-xl shadow p-6 border border-gray-100">
            <!-- Faktúrované služby -->
            <div v-if="tab === 1">
                <div class="mb-4 flex items-center gap-3 p-4 rounded-lg bg-slate-50 border border-slate-100">
                    <svg class="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24" title="Súčet cien">
                        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                            fill="none" />
                        <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <span class="font-semibold text-slate-900">Suma všetkých služieb:</span>
                    <span class="ml-auto text-xl font-bold text-teal-700">{{items.reduce((sum, i) => sum +
                        (isNaN(parseFloat(i.price ?? '0')) ? 0 : parseFloat(i.price ?? '0')), 0).toFixed(2)}} €</span>
                </div>
                <div class="shadow overflow-hidden sm:rounded-md">
                    <div class="px-4 py-5 bg-white sm:p-6 flex flex-col gap-4">
                        <div v-for="(item, index) in items" :key="index"
                            class="transition hover:bg-teal-50 rounded-lg p-3 group border border-slate-100">
                            <div class="grid grid-cols-9 gap-6 items-center">
                                <div class="col-span-1 text-center self-center text-slate-400 font-semibold">#{{ index +
                                    1
                                }}</div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="description"
                                        class="block text-sm font-medium text-gray-700">Popis</label>
                                    <div>{{ item.description }}</div>
                                </div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="amount" class="block text-sm font-medium text-gray-700">Suma</label>
                                    <FormKit type="text" v-model="item.price" name="amount" id="amount"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="col-span-2 sm:col-span-2">
                                    <label for="amount" class="block text-sm font-medium text-gray-700">DPH</label>
                                    <FormKit type="text" v-model="item.price_vat" name="amount" id="amount"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!--Súčasná spoločnosť-->
            <div v-if="tab === 2">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Aktuálna spoločnosť -->
                    <div>
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="flex flex-col w-full">
                                    <div class="text-2xl font-bold py-4">Aktuálna spoločnosť</div>
                                    <div class="flex flex-row w-full">
                                        <div class="flex flex-col w-full">
                                            <div class="flex flex-row gap-4 w-full">
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700">Meno
                                                        spoločnosti</label>
                                                    <FormKit type="text" v-model="actual_company.obchodne_meno"
                                                        id="obchodne_meno"
                                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                </div>
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700">IČO</label>
                                                    <FormKit type="text" v-model="actual_company.ico" id="ico"
                                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Zmenená spoločnosť -->
                    <div>
                        <div class="shadow overflow-hidden sm:rounded-md">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="flex flex-col w-full">
                                    <div class="text-2xl font-bold py-4">Zmenená spoločnosť</div>
                                    <div class="flex flex-row w-full">
                                        <div class="flex flex-col w-full">
                                            <div class="flex flex-row gap-4">
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700">Meno
                                                        spoločnosti</label>
                                                    <FormKit type="text" v-model="updated_company.obchodne_meno"
                                                        id="obchodne_meno"
                                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-row w-full">
                                        <div class="flex flex-col w-full">
                                            <label class="block text-2xl font-bold text-gray-700 py-4">Nová adresa
                                                trvalého bydliska</label>
                                            <div class="flex flex-row w-full gap-2"
                                                v-if="updated_company.new_pernament_address != undefined">
                                                <div class="flex flex-col w-full">
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700">PSČ</label>
                                                        <FormKit type="text"
                                                            v-model="updated_company.new_pernament_address.psc" id="psc"
                                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                    </div>
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700">Mesto</label>
                                                        <FormKit type="text"
                                                            v-model="updated_company.new_pernament_address.city"
                                                            id="city"
                                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                    </div>
                                                </div>
                                                <div class="flex flex-col w-full">
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700">Ulica</label>
                                                        <FormKit type="text"
                                                            v-model="updated_company.new_pernament_address.street"
                                                            id="street"
                                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                    </div>
                                                    <div>
                                                        <label class="block text-sm font-medium text-gray-700">Popisné
                                                            číslo</label>
                                                        <FormKit type="text"
                                                            v-model="updated_company.new_pernament_address.street_number"
                                                            id="number"
                                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                    </div>
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700">Orientačné
                                                            číslo</label>
                                                        <FormKit type="text"
                                                            v-model="updated_company.new_pernament_address.street_number2"
                                                            id="number2"
                                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="flex flex-col w-full"
                                                v-if="updated_company.new_bussiness_address != undefined && updated_company.new_bussiness_address.psc != null">
                                                <div v-if="updated_company.new_bussiness_address.psc != undefined">
                                                    <label class="block text-2xl font-bold text-gray-700 py-4">Nová
                                                        adresa miesta podnikania (sídla)</label>
                                                    <div class="flex flex-row w-full gap-2">
                                                        <div class="flex flex-col w-full">
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700">PSČ</label>
                                                                <FormKit type="text"
                                                                    v-model="updated_company.new_bussiness_address.psc"
                                                                    id="psc"
                                                                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700">Mesto</label>
                                                                <FormKit type="text"
                                                                    v-model="updated_company.new_bussiness_address.city"
                                                                    id="city"
                                                                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                            </div>
                                                        </div>
                                                        <div class="flex flex-col w-full">
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700">Ulica</label>
                                                                <FormKit type="text"
                                                                    v-model="updated_company.new_bussiness_address.street"
                                                                    id="street"
                                                                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700">Popisné
                                                                    číslo</label>
                                                                <FormKit type="text"
                                                                    v-model="updated_company.new_bussiness_address.street_number"
                                                                    id="number"
                                                                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700">Orientačné
                                                                    číslo</label>
                                                                <FormKit type="text"
                                                                    v-model="updated_company.new_bussiness_address.street_number2"
                                                                    id="number"
                                                                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-else>
                                                    <label class="block text-2xl font-bold text-gray-700 py-4">Nová
                                                        adresa miesta podnikania (sídla)</label>
                                                    <FormKit type="text" v-model="updated_company.new_bussiness_address"
                                                        name="druh_priestoru" id="druh_priestoru"
                                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                                                    </FormKit>
                                                </div>
                                                <div
                                                    v-if="updated_company.new_bussiness_address.who_is_owner != undefined">
                                                    <label class="block text-sm font-medium text-gray-700">Vlastník
                                                        sídla</label>
                                                    <FormKit type="text"
                                                        v-model="updated_company.new_bussiness_address.who_is_owner"
                                                        id="number"
                                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                </div>
                                            </div>
                                            <div v-if="updated_company.new_establisment_address != undefined">
                                                <label class="block text-2xl font-bold text-gray-700 py-4">Nová adresa
                                                    prevádzkarne</label>
                                                <div class="flex flex-row w-full gap-2">
                                                    <div class="flex flex-col w-full">
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700">PSČ</label>
                                                            <FormKit type="text"
                                                                v-model="updated_company.new_establisment_address.psc"
                                                                id="psc"
                                                                class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                        </div>
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700">Mesto</label>
                                                            <FormKit type="text"
                                                                v-model="updated_company.new_establisment_address.city"
                                                                id="city"
                                                                class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                        </div>
                                                    </div>
                                                    <div class="flex flex-col w-full">
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700">Ulica</label>
                                                            <FormKit type="text"
                                                                v-model="updated_company.new_establisment_address.street"
                                                                id="street"
                                                                class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                        </div>
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700">Popisné
                                                                číslo</label>
                                                            <FormKit type="text"
                                                                v-model="updated_company.new_establisment_address.street_number"
                                                                id="number"
                                                                class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                        </div>
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700">Orientačné
                                                                číslo</label>
                                                            <FormKit type="text"
                                                                v-model="updated_company.new_establisment_address.street_number2"
                                                                id="number"
                                                                class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                    <div class="flex flex-row w-full"
                                        v-if="updated_company.new_establisment_address != undefined && updated_company.new_establisment_address.which_subject_of_bussiness != undefined">
                                        <div class="flex flex-col w-full">
                                            <label class="block text-2xl font-bold text-gray-700 py-4">Predmety
                                                podnikania</label>
                                            <div class="flex flex-row">
                                                {{ updated_company.new_establisment_address.which_subject_of_bussiness
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-row w-full" v-if="updated_company.ine_zmeny">
                                        <div class="flex flex-col w-full">
                                            <label class="block text-2xl font-bold text-gray-700 py-4">Iné
                                                zmeny</label>
                                            <div class="flex flex-row">
                                                <div>
                                                    <FormKit type="textarea" v-model="updated_company.ine_zmeny"
                                                        id="ine_zmeny"
                                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <!--PODNIKATELSKE UDAJE-->
            <div v-if="tab === 3">
                <div class="mt-5">
                    <div class="shadow overflow-hidden">
                        <div class="px-4 py-5 bg-white sm:p-6">
                            <div class="flex flex-row pb-4">
                                <div class="flex flex-col w-full">
                                    <label for="company_id" class="text-xl font-bold">Názov spoločnosti</label>
                                    <FormKit type="text" v-model="currentCompany.name" name="first_name" id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Krstné
                                        meno</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].first_name" name="first_name"
                                        id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="first_name"
                                        class="block text-sm font-medium text-gray-700">Priezvisko</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].last_name" name="first_name"
                                        id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="first_name"
                                        class="block text-sm font-medium text-gray-700">Pohlavie</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].gender" name="first_name"
                                        id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>

                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Titul
                                        pred
                                        menom</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].title_before"
                                        name="first_name" id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Titul za
                                        menom</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].title_after"
                                        name="first_name" id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>

                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col w-full">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Telefonné
                                        číslo</label>
                                    <FormKit type="text" v-model="currentUser.phone" name="first_name" id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="dob2" class="block text-sm font-medium text-gray-700">Dátum
                                        narodenia</label>
                                    <FormKit type="date" v-model="currentCompanyMembers[0].date_of_birth" name="dob"
                                        id="dob2"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Rodné
                                        číslo</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].rodne_cislo"
                                        name="first_name" id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="first_name"
                                        class="block text-sm font-medium text-gray-700">Národnosť</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].nationality"
                                        name="first_name" id="first_name"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>

                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col w-full">
                                    <label for="city" class="block text-sm font-medium text-gray-700">Mesto</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].city" name="city" id="city"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="street" class="block text-sm font-medium text-gray-700">Ulica</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].street" name="street"
                                        id="street"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="street_number" class="block text-sm font-medium text-gray-700">Súpisné
                                        číslo</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].street_number"
                                        name="street_number" id="street_number"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="street_number2"
                                        class="block text-sm font-medium text-gray-700">Orientačné
                                        číslo</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].street_number2"
                                        name="street_number2" id="street_number2"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="psc" class="block text-sm font-medium text-gray-700">PSČ</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].psc" name="psc" id="psc"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="country" class="block text-sm font-medium text-gray-700">Krajina</label>
                                    <FormKit type="text" v-model="currentCompanyMembers[0].country" name="country"
                                        id="country"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>

                            <div class="flex flex-row gap-4">
                                <div class="flex flex-col w-full">
                                    <label for="registracia" class="block text-sm font-medium text-gray-700">Registrácia
                                        živnosti ku dňu?</label>
                                    <FormKit type="text" v-model="currentCompany.zaciatok_opravnenia" name="registracia"
                                        id="registracia"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="py-3 bg-gray-50 text-right">
                <button type="submit"
                    class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                    Uložiť
                </button>
            </div>
        </div>
    </div>
</template>